/**
 * @fileoverview WIP ledger — unbilled approved time per matter (MOR-906 §7.4)
 *
 * Aged-WIP view for partners: approved billable time not yet invoiced,
 * valued from rate snapshots, bucketed 0–30 / 31–60 / 61–90 / 90+ days.
 *
 * Two ways to reduce what a matter is carrying, and they are not the same
 * thing. Expanding a matter lists its entries for the write-off flow: select
 * → write off with a reason, which drops that time out of WIP entirely and
 * feeds realisation analytics (reversible while unbilled). A provision
 * instead reduces the *net* figure while the WIP stays on the file and keeps
 * ageing — the partner's view of what is actually recoverable.
 *
 * Aging buckets stay gross: how old the work is has nothing to do with how
 * much of it the firm expects to recover.
 *
 * A firm carries WIP on every open matter, so the ledger is filterable by
 * matter and the footer totals follow the filter rather than the firm. The
 * export carries the same filter, as a matter summary plus the underlying
 * time (see wipExport.ts).
 *
 * Invoice generation hangs off this surface in the invoicing slice.
 */

import { useState } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useConvex, useMutation } from "convex/react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  TriangleAlert,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { extractErrorMessage } from "@/utils/errors";
import { Button } from "@/ui/primitives";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Label } from "@/ui/primitives/label";
import { Spinner } from "@/ui/primitives/spinner";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import {
  exportWipLedgerToExcel,
  provisionMethodLabel,
  type WipLedgerMatter,
} from "./wipExport";
import { formatAud, formatUnitsAsHours } from "./format";
import { WipProvisionDialog } from "./WipProvisionDialog";

export function WipLedger({ orgId }: { orgId: Id<"organizations"> }) {
  const today = format(new Date(), "yyyy-MM-dd");
  const { data } = useQuery(
    convexQuery(api.timesheets.reports.wipLedger, { orgId, today }),
  );
  const convex = useConvex();
  const [expandedJobId, setExpandedJobId] = useState<Id<"jobs"> | null>(null);
  const [provisioning, setProvisioning] = useState<Id<"jobs"> | null>(null);
  const [matterFilter, setMatterFilter] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  if (data === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  if (data.matters.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-xl bg-background text-sm text-muted-foreground shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        No approved billable time yet — WIP builds as timesheets are approved.
      </div>
    );
  }

  const matterOptions = [...data.matters]
    .sort((a, b) => a.jobName.localeCompare(b.jobName))
    .map((m) => ({
      value: m.jobId as string,
      label: m.jobCode ? `${m.jobName} · ${m.jobCode}` : m.jobName,
    }));

  const selected = new Set(matterFilter);
  const visible =
    selected.size === 0
      ? data.matters
      : data.matters.filter((m) => selected.has(m.jobId as string));

  // Totals follow the filter: a footer that keeps reporting the whole firm
  // while the rows show three matters is worse than no footer.
  const totals = visible.reduce(
    (acc, m) => ({
      units: acc.units + m.units,
      value: acc.value + m.value,
      netValue: acc.netValue + m.netValue,
      provisionValue: acc.provisionValue + (m.provision?.amount ?? 0),
      writtenOffValue: acc.writtenOffValue + m.writtenOffValue,
    }),
    { units: 0, value: 0, netValue: 0, provisionValue: 0, writtenOffValue: 0 },
  );

  const filterNote =
    selected.size > 0
      ? `Filtered to ${visible.length} of ${data.matters.length} matters`
      : undefined;

  const handleExport = async () => {
    setExporting(true);
    try {
      // The drill-down is subscribed per expanded matter; the export needs
      // every visible matter's time, so it is fetched once on click.
      const entries = await convex.query(
        api.timesheets.reports.wipLedgerEntries,
        {
          orgId,
          jobIds: selected.size > 0 ? visible.map((m) => m.jobId) : undefined,
        },
      );
      exportWipLedgerToExcel({
        // Export what is on screen, filter and all — a sheet that quietly
        // holds more than the table did is a trap.
        matters: visible,
        totals,
        entries,
        unitMinutes: data.unitMinutes,
        today,
        filterNote,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not export the ledger."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <MultiSearchableSelect
          value={matterFilter}
          onValueChange={setMatterFilter}
          options={matterOptions}
          placeholder="All matters"
          searchPlaceholder="Search matters..."
          emptyMessage="No matters with WIP."
          className="w-72"
        />
        <span className="text-xs text-muted-foreground">
          {selected.size === 0
            ? `${data.matters.length} ${data.matters.length === 1 ? "matter" : "matters"}`
            : `${visible.length} of ${data.matters.length} matters`}
        </span>
        {selected.size > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setMatterFilter([])}
          >
            Clear filter
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="ml-auto h-8"
          disabled={exporting}
          onClick={() => void handleExport()}
        >
          {exporting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
          Export Excel
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Matter</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Gross WIP</TableHead>
                <TableHead className="text-right">Provision</TableHead>
                <TableHead className="text-right">Net WIP</TableHead>
                <TableHead className="text-right">Written off</TableHead>
                <TableHead className="text-right">0–30d</TableHead>
                <TableHead className="text-right">31–60d</TableHead>
                <TableHead className="text-right">61–90d</TableHead>
                <TableHead className="text-right">90d+</TableHead>
                <TableHead className="text-right">Oldest</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((m) => (
                <MatterRows
                  key={m.jobId}
                  matter={m}
                  unitMinutes={data.unitMinutes}
                  expanded={expandedJobId === m.jobId}
                  onToggle={() =>
                    setExpandedJobId((prev) =>
                      prev === m.jobId ? null : m.jobId,
                    )
                  }
                  onSetProvision={() => setProvisioning(m.jobId)}
                />
              ))}
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableCell />
                <TableCell className="text-sm font-semibold">
                  {selected.size > 0 ? "Total (filtered)" : "Total"}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums">
                  {formatUnitsAsHours(totals.units, data.unitMinutes)}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums">
                  {formatAud(totals.value)}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums text-amber-600 dark:text-amber-500">
                  {totals.provisionValue > 0
                    ? `(${formatAud(totals.provisionValue)})`
                    : "—"}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums">
                  {formatAud(totals.netValue)}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold tabular-nums text-muted-foreground">
                  {totals.writtenOffValue > 0
                    ? formatAud(totals.writtenOffValue)
                    : "—"}
                </TableCell>
                <TableCell colSpan={5} />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Gross WIP is approved billable time not yet invoiced. Net WIP is gross
        less any provision — the net realisable value the firm expects to
        recover. A provision leaves the time on the file and ageing; writing off
        removes it from WIP altogether and feeds realisation analytics.
        Percentage and "net of" provisions recalculate on their own as time is
        posted or written off. Entries without a resolvable rate (marked *) are
        counted in hours but not value.
      </p>

      <WipProvisionDialog
        orgId={orgId}
        jobId={provisioning}
        open={provisioning !== null}
        onOpenChange={(next) => {
          if (!next) setProvisioning(null);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Matter row + write-off drill-down
// ---------------------------------------------------------------------------

/** The row shape, with the job id narrowed to what this component clicks. */
type Matter = WipLedgerMatter & { jobId: Id<"jobs"> };

function MatterRows({
  matter,
  unitMinutes,
  expanded,
  onToggle,
  onSetProvision,
}: {
  matter: Matter;
  unitMinutes: number;
  expanded: boolean;
  onToggle: () => void;
  onSetProvision: () => void;
}) {
  const bucket = (v: number, warn = false) =>
    v > 0 ? (
      <span className={warn ? "text-red-600 dark:text-red-400" : undefined}>
        {formatAud(v)}
      </span>
    ) : (
      "—"
    );

  const provisioned = matter.provision;

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="pr-0">
          {expanded ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{matter.jobName}</span>
            {matter.jobCode && (
              <span className="text-xs text-muted-foreground">
                {matter.jobCode}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right text-sm tabular-nums">
          {formatUnitsAsHours(matter.units, unitMinutes)}
          {matter.unratedUnits > 0 && (
            <span
              className="ml-1 text-xs text-amber-600"
              title={`${formatUnitsAsHours(matter.unratedUnits, unitMinutes)} have no rate — assign grades and rates in Timesheet Settings`}
            >
              *
            </span>
          )}
        </TableCell>
        <TableCell className="text-right text-sm font-medium tabular-nums">
          {formatAud(matter.value)}
        </TableCell>
        <TableCell
          className="text-right"
          // The row toggles the drill-down; the provision cell must not.
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-auto flex-col items-end gap-0 px-2 py-1 font-normal"
            onClick={onSetProvision}
            title={
              provisioned
                ? "Change or release this provision"
                : "Provide against this matter's WIP"
            }
          >
            {provisioned === null ? (
              <span className="text-xs text-muted-foreground">Set</span>
            ) : provisioned.amount === null ? (
              <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
                <TriangleAlert className="size-3" />
                Needs input
              </span>
            ) : (
              <>
                <span className="text-sm tabular-nums text-amber-600 dark:text-amber-500">
                  ({formatAud(provisioned.amount)})
                </span>
                <span className="text-[11px] leading-tight text-muted-foreground">
                  {provisionMethodLabel(provisioned)}
                  {provisioned.clamped && " · capped"}
                </span>
              </>
            )}
          </Button>
        </TableCell>
        <TableCell className="text-right text-sm font-semibold tabular-nums">
          {formatAud(matter.netValue)}
        </TableCell>
        <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
          {matter.writtenOffValue > 0 ? formatAud(matter.writtenOffValue) : "—"}
        </TableCell>
        <TableCell className="text-right text-sm tabular-nums">
          {bucket(matter.aging.current)}
        </TableCell>
        <TableCell className="text-right text-sm tabular-nums">
          {bucket(matter.aging.days31to60)}
        </TableCell>
        <TableCell className="text-right text-sm tabular-nums">
          {bucket(matter.aging.days61to90)}
        </TableCell>
        <TableCell className="text-right text-sm tabular-nums">
          {bucket(matter.aging.over90, true)}
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
          {matter.oldestDate || "—"}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={12} className="bg-muted/30 p-0">
            <MatterEntries jobId={matter.jobId} unitMinutes={unitMinutes} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function MatterEntries({
  jobId,
  unitMinutes,
}: {
  jobId: Id<"jobs">;
  unitMinutes: number;
}) {
  const { data: entries } = useQuery(
    convexQuery(api.timesheets.reports.wipMatterEntries, { jobId }),
  );
  const writeOffEntries = useMutation(api.timesheets.entries.writeOffEntries);
  const undoWriteOff = useMutation(api.timesheets.entries.undoWriteOff);

  const [selected, setSelected] = useState<Set<Id<"timeEntries">>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (entries === undefined) {
    return (
      <div className="flex h-24 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  const active = entries.filter((e) => !e.writtenOff);
  const toggle = (id: Id<"timeEntries">) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleWriteOff = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const count = await writeOffEntries({
        entryIds: [...selected],
        reason: reason.trim(),
      });
      toast.success(
        count === 1 ? "1 entry written off." : `${count} entries written off.`,
      );
      setSelected(new Set());
      setDialogOpen(false);
      setReason("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not write off the entries."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {active.length} unbilled {active.length === 1 ? "entry" : "entries"}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={selected.size === 0 || busy}
          onClick={() => setDialogOpen(true)}
        >
          Write off{" "}
          {selected.size > 0 ? `${selected.size} selected` : "selected"}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Date</TableHead>
            <TableHead>Staff</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Narrative</TableHead>
            <TableHead className="text-right">Hours</TableHead>
            <TableHead className="text-right">Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((e) => (
            <TableRow
              key={e._id}
              className={e.writtenOff ? "opacity-60" : undefined}
            >
              <TableCell>
                {e.writtenOff ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 text-muted-foreground"
                    title={`Written off: ${e.writeOffReason ?? ""} — click to restore`}
                    onClick={() =>
                      undoWriteOff({ entryId: e._id }).catch((err: unknown) =>
                        toast.error(
                          extractErrorMessage(
                            err,
                            "Could not restore the entry.",
                          ),
                        ),
                      )
                    }
                  >
                    <Undo2 className="size-3.5" />
                  </Button>
                ) : (
                  <Checkbox
                    checked={selected.has(e._id)}
                    onCheckedChange={() => toggle(e._id)}
                    aria-label={`Select entry on ${e.date}`}
                  />
                )}
              </TableCell>
              <TableCell className="text-xs tabular-nums">{e.date}</TableCell>
              <TableCell
                className="text-xs"
                title={
                  e.staffCode ? `IPS staff code: ${e.staffCode}` : undefined
                }
              >
                {e.staff}
              </TableCell>
              <TableCell className="text-xs">{e.categoryName}</TableCell>
              <TableCell
                className={`max-w-96 text-xs ${e.writtenOff ? "line-through" : ""}`}
              >
                {e.narrative}
              </TableCell>
              <TableCell className="text-right text-xs tabular-nums">
                {formatUnitsAsHours(e.units, unitMinutes)}
              </TableCell>
              <TableCell className="text-right text-xs tabular-nums">
                {e.value !== undefined ? formatAud(e.value) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Write off selected entries</DialogTitle>
            <DialogDescription>
              Written-off time leaves the WIP ledger and is excluded from the
              REM pack, but stays on record and feeds realisation analytics. You
              can restore it while it remains unbilled.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="write-off-reason">Reason</Label>
            <Textarea
              id="write-off-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Estate has insufficient realisations to recover this work."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!reason.trim() || busy}
              onClick={() => void handleWriteOff()}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              Write off{" "}
              {selected.size === 1 ? "entry" : `${selected.size} entries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
